import React, { useState } from 'react';
import { Alert, Form, message, Modal as AntdModal, Popover } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { DatePicker, Input } from 'components/Form';
import { Button, Modal, Table } from 'ui';
import CopyValue from 'ui/CopyValue';
import { useT } from 'hooks/Locale/useT';
import useQueryApiClient from 'utils/useApiClient';
import useHandleError from 'utils/useHandleError';
import { config } from 'config/config';

const API_KEYS_URL = '/api/v1/api-keys';

export type ApiKeysOwnerType = 'organization' | 'customer';

interface ApiKeysTabProps {
    type: ApiKeysOwnerType;
    selectedResellerId: number;
    selectedCustomerId: number;
}

interface ApiKeyRow {
    id: number;
    name: string | null;
    created_at: string | null;
    last_used_at: string | null;
    expires_at: string | null;
}

interface CreateFormValues {
    name: string;
    expiresAt?: Moment | null;
}

/**
 * API keys of the reseller/customer selected on the Customize environment page. The
 * gateway resolves and authorises the owner from `ownerType` + `ownerId`, so this tab
 * follows the selection exactly like its sibling tabs. The plaintext key is shown once.
 */
const ApiKeysTab: React.FC<ApiKeysTabProps> = ({
    type,
    selectedResellerId,
    selectedCustomerId,
}) => {
    const t = useT();
    const [form] = Form.useForm();
    const [handleError] = useHandleError();
    const [reload, setReload] = useState<boolean>(false);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [createdSecret, setCreatedSecret] = useState<string | null>(null);
    const [openActionsRow, setOpenActionsRow] = useState<number | null>(null);

    const ownerId = selectedCustomerId ? selectedCustomerId : selectedResellerId;
    const owner = { ownerType: type, ownerId };
    const ownerQuery = `ownerType=${type}&ownerId=${ownerId}`;

    const closeCreateModal = (): void => {
        setShowCreateModal(false);
        form.resetFields();
    };

    const { appendData: createKey, isLoading: isCreating } = useQueryApiClient({
        request: { url: API_KEYS_URL, method: 'POST', disableOnMount: true },
        onSuccess: (response: { secret?: string }): void => {
            closeCreateModal();
            setCreatedSecret(response?.secret ?? null);
            setReload(true);
        },
        onError: handleError,
    });

    const { appendData: revokeKey } = useQueryApiClient({
        request: {
            url: `${API_KEYS_URL}/:id?${ownerQuery}`,
            method: 'DELETE',
            disableOnMount: true,
        },
        onSuccess: (): void => {
            message.success(t('api_keys.revoked', 'API key revoked'));
            setReload(true);
        },
        onError: handleError,
    });

    const submitCreate = (values: CreateFormValues): void => {
        createKey({
            name: values.name,
            expiresAt: values.expiresAt ? values.expiresAt.format(config.DBDateFormat) : null,
            ...owner,
        });
    };

    const confirmRevoke = (row: ApiKeyRow): void => {
        setOpenActionsRow(null);
        AntdModal.confirm({
            title: t('general.attention', 'Attention'),
            content: t(
                'api_keys.revoke_confirm',
                'Revoke this key? Integrations using it will stop working immediately.',
            ),
            icon: <ExclamationCircleOutlined />,
            okText: t('general.yes', 'Yes'),
            cancelText: t('general.no', 'No'),
            onOk: () => revokeKey(undefined, { id: row.id }),
        });
    };

    const formatDateTime = (value: string | null): string =>
        value ? moment(value).format(config.defaultFullDateFormat) : '-';

    const isExpired = (value: string | null): boolean =>
        !!value && moment(value, config.DBDateFormat).isBefore(moment(), 'day');

    const columns = [
        {
            title: t('general.name', 'Name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('general.created_at', 'Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: formatDateTime,
        },
        {
            title: t('api_keys.last_used_at', 'Last used'),
            dataIndex: 'last_used_at',
            key: 'last_used_at',
            render: formatDateTime,
        },
        {
            title: t('general.valid_until', 'Valid until'),
            dataIndex: 'expires_at',
            key: 'expires_at',
            render: (value: string | null): React.ReactNode => {
                if (!value) {
                    return '-';
                }

                return (
                    <>
                        {moment(value, config.DBDateFormat).format(config.defaultDateFormat)}
                        {isExpired(value) && ` (${t('general.expired', 'expired')})`}
                    </>
                );
            },
        },
        {
            title: t('general.actions', 'Actions'),
            key: 'actions',
            render: (_: unknown, row: ApiKeyRow): React.ReactNode => (
                <Popover
                    open={row.id === openActionsRow}
                    onOpenChange={(visible: boolean) => setOpenActionsRow(visible ? row.id : null)}
                    trigger="click"
                    placement="bottomRight"
                    arrowPointAtCenter
                    content={
                        <div className="popover-item" onClick={() => confirmRevoke(row)}>
                            {t('general.delete', 'Delete')}
                        </div>
                    }
                >
                    <div style={{ width: '100%', cursor: 'pointer', textAlign: 'center' }}>
                        <i className="fal fa-ellipsis-v" style={{ fontSize: '16px' }} />
                    </div>
                </Popover>
            ),
        },
    ];

    if (!ownerId) {
        return (
            <Alert
                message={t(
                    `system.environment.please_select_a_${type === 'organization' ? 'reseller' : 'customer'}`,
                )}
                type="info"
                showIcon
            />
        );
    }

    return (
        <>
            <div className="search-bar-row">
                <Button
                    type="primary"
                    message="api_keys.create"
                    onClick={() => setShowCreateModal(true)}
                />
            </div>
            <Table
                key={ownerQuery}
                columns={columns}
                url={API_KEYS_URL}
                filter={ownerQuery}
                reload={reload}
                setReload={setReload}
                rowKey="id"
                rowSelection={false}
            />
            <Modal
                open={showCreateModal}
                title={t('api_keys.create', 'Create API key')}
                onOk={() => form.submit()}
                onCancel={closeCreateModal}
                isLoading={isCreating}
                okText={t('general.submit', 'Submit')}
                cancelText={t('general.cancel', 'Cancel')}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={submitCreate}>
                    <Input
                        name="name"
                        label={t('general.name', 'Name')}
                        customLayout
                        maxLength={100}
                        customRules={[
                            {
                                required: true,
                                max: 100,
                                message: t('validation.field_required', 'Field is required'),
                            },
                        ]}
                    />
                    <DatePicker
                        name="expiresAt"
                        label={t('general.valid_until', 'Valid until')}
                        customLayout
                        format={config.defaultDateFormat}
                        disabledDate={(current: Moment) => current.isSameOrBefore(moment(), 'day')}
                        style={{ width: '100%' }}
                    />
                </Form>
            </Modal>
            <Modal
                open={createdSecret !== null}
                title={t('api_keys.created', 'API key created')}
                onOk={() => setCreatedSecret(null)}
                onCancel={() => setCreatedSecret(null)}
                okText={t('general.close', 'Close')}
                cancelButtonProps={{ style: { display: 'none' } }}
                maskClosable={false}
            >
                <p>
                    {t(
                        'api_keys.secret_notice',
                        'Copy the key now. It is shown only once and cannot be recovered later.',
                    )}
                </p>
                <CopyValue value={createdSecret} fullWidth />
                <p>
                    {t(
                        'api_keys.header_hint',
                        'Send the key in the X-CS-HTTP-REPORT-SECRET request header.',
                    )}
                </p>
            </Modal>
        </>
    );
};

export default ApiKeysTab;
